import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { PaymentMethod } from '@stripe/stripe-js';

import { media } from 'context';
import { ANIMATION_DURATION, APP_BANNER_HEIGHT, IconButton, Layout, PillButton, StickyContainer, Text } from 'ds';
import StripePaymentMethodModal from 'ds/StripePaymentMethodModal';
import { listPaymentMethods } from 'ds/StripePaymentMethodModal/requests';
import UpdateStripePaymentMethodModal from 'ds/UpdateStripePaymentMethodModal';
import { TENANT_ADMIN_PATH, TENANT_ADMIN_PAYMENT_METHODS_PATH } from 'routes/paths';
import { actions as tenancyActions } from 'store/Tenancy';
import {
  selectBuildingUnitTenanciesWithRent,
  selectPaymentMethods,
  selectRentPaymentMethod,
  selectServicesPaymentMethod
} from 'store/Tenancy/selectors';
import { selectUIState } from 'store/UI/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectTenancyId } from 'store/v2/User/selectors';
import MissingContentPlaceholder from 'ux/Tenant/PantryAndSupplies/MissingContentPlaceholder';

import MobileTenantAdminPaymentCardMenuModal from './MobileTenantAdminPaymentCardMenuModal';
import ServicesAndRentPaymentCard from './ServicesAndRentPaymentCard';
import TenantAdminPaymentMethodCard from './TenantAdminPaymentMethodCard';
import { getBuildingUnitTenancies } from '../requests';

interface PaymentMethodsProps {}

const PaymentMethods: React.FC<PaymentMethodsProps> = () => {
  const { prevPathname } = useAppSelector(selectUIState);
  const backButtonHref =
    prevPathname && prevPathname !== TENANT_ADMIN_PAYMENT_METHODS_PATH ? prevPathname : TENANT_ADMIN_PATH;
  const { isMobile } = useContext(media);
  const dispatch = useAppDispatch();
  const tenancyId = useAppSelector(selectTenancyId);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [showRentPaymentMethodModal, setShowRentPaymentMethodModal] = useState(false);
  const [showUpdatePaymentMethodModal, setShowUpdatePaymentMethodModal] = useState(false);
  const [selectedUpdatePaymentMethodType, setSelectedUpdatePaymentMethodType] = useState<'services' | 'rent' | null>(
    null
  );
  const { appBannerType } = useAppSelector(selectUIState);
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const servicesPaymentMethod = useAppSelector(selectServicesPaymentMethod);
  const rentPaymentMethod = useAppSelector(selectRentPaymentMethod);
  const buildingUnitTenanciesWithRent = useAppSelector(selectBuildingUnitTenanciesWithRent);
  const paymentMethods = useAppSelector(selectPaymentMethods);
  const rentAvailable = buildingUnitTenanciesWithRent && buildingUnitTenanciesWithRent?.length > 0;
  const [onlyAddingNewPaymentMethod, setOnlyAddingNewPaymentMethod] = useState(false);
  const [showMobileTenantAdminPaymentCardMenu, setShowMobileTenantAdminPaymentCardMenu] = useState(false);
  const [selectedPaymentMethodForCardMenu, setSelectedPaymentMethodForCardMenu] = useState<PaymentMethod | null>(null);

  const loadBuildingUnitTenancies = useCallback(() => {
    if (!tenancyId) return;
    getBuildingUnitTenancies({ tenancyId }).then(({ data }) => dispatch(tenancyActions.setBuildingUnitTenancies(data)));
  }, [tenancyId, dispatch]);

  const loadPaymentMethods = useCallback(() => {
    if (!tenancyId) return;
    listPaymentMethods(tenancyId).then(({ data }) => {
      dispatch(tenancyActions.setPaymentMethods(data));

      const servicesPaymentMethod = data.find(paymentMethod => paymentMethod.metadata.services_payment_method);
      const rentPaymentMethod = data.find(paymentMethod => paymentMethod.metadata.rent_payment_method);
      dispatch(tenancyActions.setServicesPaymentMethod(servicesPaymentMethod));
      dispatch(tenancyActions.setRentPaymentMethod(rentPaymentMethod));
    });
  }, [tenancyId, dispatch]);

  const closePaymentMethodModal = () => {
    setShowPaymentMethodModal(false);
    setOnlyAddingNewPaymentMethod(false);
  };

  const closeRentPaymentMethodModal = () => {
    setShowRentPaymentMethodModal(false);
  };

  useEffect(() => {
    if (!buildingUnitTenanciesWithRent || !paymentMethods) {
      loadBuildingUnitTenancies();
      loadPaymentMethods();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!tenancyId || !buildingUnitTenanciesWithRent || !paymentMethods) return null;
  const nonDefaultPaymentMethods = paymentMethods.filter(
    paymentMethod => paymentMethod.id !== servicesPaymentMethod?.id && paymentMethod.id !== rentPaymentMethod?.id
  );

  return (
    <Layout
      flex
      flexGrow={1}
      direction="column"
      paddingBottom={60}
      color="blue-50"
      gap={isMobile ? 24 : 16}
      boxShadow="2px 0 22px 0 rgba(34.1176, 28.6275, 42.7451, 0.1200)"
    >
      <StickyContainer
        __style={{ alignSelf: 'flex-start' }}
        zIndex={10}
        width="100%"
        color="blue-50"
        paddingY={isMobile ? 10 : 24}
        paddingX={isMobile ? undefined : 40}
        paddingRight={isMobile ? 72 : undefined}
        __ref={containerRef}
        top={appBannerType ? APP_BANNER_HEIGHT : 0}
        height={isMobile ? 60 : 76}
        align={'center'}
      >
        <Layout
          direction="row"
          height={isMobile ? 60 : 76}
          width="100%"
          align="center"
          gap={12}
          paddingLeft={isMobile ? 16 : undefined}
        >
          <Layout>
            <IconButton
              name={isMobile ? 'leftChevron' : 'leftArrow'}
              size="lg"
              iconSize={isMobile ? 'lg' : undefined}
              type="noBackground"
              href={backButtonHref}
            />
          </Layout>
          <Layout width={isMobile ? '100%' : undefined} justify={isMobile ? 'center' : undefined}>
            <Text size={isMobile ? 'body-md' : 'headline-md'} semibold={isMobile}>
              Payment methods
            </Text>
          </Layout>
        </Layout>
      </StickyContainer>
      <Layout width="100%" align="center" direction="column" gap={36} paddingX={isMobile ? 24 : undefined}>
        {rentAvailable && (
          <Layout width={isMobile ? '100%' : undefined} direction={isMobile ? 'column' : 'row'} gap={16}>
            <ServicesAndRentPaymentCard
              paymentMethod={servicesPaymentMethod}
              title="Services"
              type="services"
              onSelect={() => {
                setSelectedUpdatePaymentMethodType('services');
                setShowUpdatePaymentMethodModal(true);
              }}
            />
            <ServicesAndRentPaymentCard
              paymentMethod={rentPaymentMethod}
              title="Rent"
              type="rent"
              onSelect={() => {
                setSelectedUpdatePaymentMethodType('rent');
                setShowUpdatePaymentMethodModal(true);
              }}
            />
          </Layout>
        )}
        {!rentAvailable && paymentMethods && paymentMethods.length === 0 && (
          <Layout width={isMobile ? '100%' : 718} direction="column" gap={16}>
            <MissingContentPlaceholder
              icon="creditCard"
              title="You don’t have any payment methods"
              subtitle="Add a payment method to pay for services."
              buttonPath={TENANT_ADMIN_PATH}
              backgroundColor="blue-100"
              customButton={
                <PillButton
                  icon="plus"
                  iconPosition="left"
                  type="purpleGradient"
                  size="md"
                  borderRadius={40}
                  text="Add payment method"
                  onClick={() => {
                    setShowPaymentMethodModal(true);
                  }}
                />
              }
            />
          </Layout>
        )}
        {paymentMethods && paymentMethods.length > 0 && (
          <Layout width={isMobile ? '100%' : 724} direction="column" gap={16}>
            {rentAvailable && (
              <Text size="body-md" semibold>
                All payment methods
              </Text>
            )}
            {servicesPaymentMethod && (
              <TenantAdminPaymentMethodCard
                paymentMethod={servicesPaymentMethod}
                defaultServicesPaymentMethod={true}
                rentAndServicesPaymentMethod={
                  rentPaymentMethod && servicesPaymentMethod
                    ? rentPaymentMethod?.id === servicesPaymentMethod?.id
                    : false
                }
                services={rentAvailable}
                setSelectedPaymentMethodForCardMenu={isMobile ? setSelectedPaymentMethodForCardMenu : undefined}
                setShowMobileTenantAdminPaymentCardMenu={isMobile ? setShowMobileTenantAdminPaymentCardMenu : undefined}
              />
            )}
            {rentPaymentMethod && rentPaymentMethod.id !== servicesPaymentMethod?.id && (
              <TenantAdminPaymentMethodCard
                paymentMethod={rentPaymentMethod}
                defaultRentPaymentMethod={true}
                setSelectedPaymentMethodForCardMenu={isMobile ? setSelectedPaymentMethodForCardMenu : undefined}
                setShowMobileTenantAdminPaymentCardMenu={isMobile ? setShowMobileTenantAdminPaymentCardMenu : undefined}
              />
            )}
            {nonDefaultPaymentMethods?.map(paymentMethod => (
              <TenantAdminPaymentMethodCard
                key={paymentMethod.id}
                paymentMethod={paymentMethod}
                defaultServicesPaymentMethod={paymentMethod.id === servicesPaymentMethod?.id}
                setSelectedPaymentMethodForCardMenu={isMobile ? setSelectedPaymentMethodForCardMenu : undefined}
                setShowMobileTenantAdminPaymentCardMenu={isMobile ? setShowMobileTenantAdminPaymentCardMenu : undefined}
              />
            ))}
            <Layout width={219}>
              <PillButton
                icon="plus"
                iconPosition="left"
                type="primaryInverse"
                size="lg"
                text="Add payment method"
                onClick={() => {
                  setOnlyAddingNewPaymentMethod(true);
                  setShowPaymentMethodModal(true);
                }}
              />
            </Layout>
          </Layout>
        )}
      </Layout>
      <StripePaymentMethodModal
        isVisible={showPaymentMethodModal}
        onUpdate={loadPaymentMethods}
        onClose={closePaymentMethodModal}
        onAddNew={paymentMethod => {
          if (!onlyAddingNewPaymentMethod) setSelectedPaymentMethod(paymentMethod);
        }}
      />
      <StripePaymentMethodModal
        rent
        isVisible={showRentPaymentMethodModal}
        onUpdate={loadPaymentMethods}
        onClose={closeRentPaymentMethodModal}
        onAddNew={paymentMethod => {
          setSelectedPaymentMethod(paymentMethod);
        }}
      />
      <UpdateStripePaymentMethodModal
        selectedPaymentMethod={selectedPaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        isVisible={showUpdatePaymentMethodModal}
        type={selectedUpdatePaymentMethodType}
        onClose={() => {
          if (showRentPaymentMethodModal || showPaymentMethodModal) return;
          setShowUpdatePaymentMethodModal(false);
          setTimeout(() => {
            setSelectedUpdatePaymentMethodType(null);
            setSelectedPaymentMethod(null);
          }, ANIMATION_DURATION);
        }}
        onSelectAddNewPaymentMethod={() => {
          if (selectedUpdatePaymentMethodType === 'rent') {
            setShowRentPaymentMethodModal(true);
          } else {
            setShowPaymentMethodModal(true);
          }
        }}
      />
      <MobileTenantAdminPaymentCardMenuModal
        isVisible={showMobileTenantAdminPaymentCardMenu}
        onClose={() => {
          setShowMobileTenantAdminPaymentCardMenu(false);
          setSelectedPaymentMethodForCardMenu(null);
        }}
        isServicesPaymentMethod={servicesPaymentMethod?.id === selectedPaymentMethodForCardMenu?.id}
        isRentPaymentMethod={rentPaymentMethod?.id === selectedPaymentMethodForCardMenu?.id}
        paymentMethod={selectedPaymentMethodForCardMenu}
      />
    </Layout>
  );
};

export default PaymentMethods;
